'use client';
import Container from '@/components/ui/Container';
import BlogCard from '@/components/blog/BlogCard';
import HeroSectionHeading from '@/components/ui/HeroSectionHeading';
import PaginationBlog from '@/components/blog/PaginationBlog';
import { useEffect, useState, useMemo } from 'react';
import { getAllCategories, getBlogsByCategory } from '@/lib/sanity/api';
import type { Blog, Category } from '@/components/type/type';
import { notFound } from 'next/navigation';
import Link from 'next/link';
import useWindowSize from '@/components/hooks/useWindowSize';
import { slugify } from '@/components/utils/helper/slugify';

type categoryProps = {
  slug: string;
};

const Category = ({ slug }: categoryProps) => {
  const { width } = useWindowSize();

  const [category, setCategory] = useState<Category | null>(null);
  const [allBlogs, setAllBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = width >= 768 ? 12 : 4;

  useEffect(() => {
    if (!slug) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const categories = await getAllCategories();

        // Find category by slug OR by normalized title (fallback if no slug)
        const foundCategory = categories.find(
          (cat) => cat.slug?.current === slug || slugify(cat.title) === slug,
        );

        if (!foundCategory) {
          console.error('Category not found for slug:', slug);
          notFound();
        }

        setCategory(foundCategory);

        const blogsData = await getBlogsByCategory(foundCategory._id);
        setAllBlogs(blogsData);
      } catch (error) {
        console.error('Error fetching category data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allBlogs.slice(startIndex, endIndex);
  }, [allBlogs, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(allBlogs.length / itemsPerPage);
  }, [allBlogs.length, itemsPerPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-lightBlue"></div>
      </div>
    );
  }

  if (!category) {
    notFound();
  }

  return (
    <div className="relative">
      <div className="absolute left-0 top-0 z-[-1] hidden h-full max-h-[800px] w-full lg:block">
        <div className="absolute bottom-0 left-0 h-[100px] w-full bg-gradient-to-t from-white to-transparent" />
      </div>
      <Container className="!max-w-[1440px] px-5 py-10 md:px-10 lg:px-20 xl:!px-[100px]">
        <div className="mb-20 flex flex-col items-center gap-[90px]">
          <HeroSectionHeading
            className="mx-auto max-w-[927px] items-center text-center"
            badgeTitle="Category"
            badgeStyle="!bg-darkGrayAlpha !font-medium"
            heading={`<span class='!text-lightBlue'>${category.title}</span> `}
          />

          <nav className="-mt-16 mb-5">
            <ol className="flex items-center gap-2 font-opt text-base">
              <li>
                <Link
                  href="/"
                  className="text-gray-600 transition-colors hover:text-lightBlue"
                >
                  Home
                </Link>
              </li>
              <li className="text-gray-400">•</li>
              <li>
                <Link
                  href="/blog"
                  className="text-gray-600 transition-colors hover:text-lightBlue"
                >
                  Blog
                </Link>
              </li>
              <li className="text-gray-400">•</li>
              <li className="text-gray-400">{category.title}</li>
            </ol>
          </nav>
        </div>

        <div className="flex flex-col gap-10">
          <div className="flex w-full flex-col items-center justify-between gap-5 md:flex-row">
            <h4 className="font-opt text-[40px] font-bold leading-[82.29px] tracking-[-2%] text-black">
              {allBlogs.length} {allBlogs.length === 1 ? 'Blog' : 'Blogs'}
            </h4>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 xl:gap-5">
            {loading ? (
              <div className="col-span-full flex items-center justify-center py-20">
                <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-lightBlue"></div>
              </div>
            ) : paginatedBlogs.length > 0 ? (
              paginatedBlogs.map((blog) => (
                <BlogCard key={blog._id} blog={blog} />
              ))
            ) : (
              <div className="col-span-full py-20 text-center">
                <p className="text-gray-500 text-xl">
                  No articles found in this category yet.
                </p>
                <Link
                  href="/blog"
                  className="mt-4 inline-block text-lightBlue hover:underline"
                >
                  Browse all articles
                </Link>
              </div>
            )}
          </div>

          {!loading && allBlogs.length > 0 && totalPages > 1 && (
            <PaginationBlog
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="mt-10"
            />
          )}
        </div>
      </Container>
    </div>
  );
};

export default Category;
